import type { ContentI18n, ContentLocale, LocalizedContent } from "./types";

function usable(v: string | undefined): v is string {
  return typeof v === "string" && v.trim().length > 0;
}

/**
 * Pick the text to render for one reader.
 *
 * The fallback chain is deliberately two links long: requested locale, then the
 * source columns. It does NOT fall through to another translation — a PT reader
 * looking at a quest that only has `es` would then get a card in a third,
 * unrequested language, which is worse than reading the author's own words.
 *
 * Pure: no React, no store, no supabase. The hook adapter lives in
 * src/hooks/useQuestContent.ts.
 */
export function resolveContent(input: {
  source: { title: string; description: string };
  i18n: ContentI18n | null | undefined;
  sourceLang: string | null;
  locale: ContentLocale;
}): LocalizedContent {
  const { source, sourceLang, locale } = input;
  const original = { title: source.title, description: source.description };

  const translated = locale === sourceLang ? undefined : input.i18n?.[locale];

  const title = usable(translated?.title) ? translated.title : source.title;
  const description = usable(translated?.description)
    ? translated.description
    : source.description;

  return {
    title,
    description,
    isTranslated: title !== source.title || description !== source.description,
    sourceLang,
    original,
  };
}
